import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Autocomplete,
} from '@mui/material';
import { Send as SendIcon, Campaign as CampaignIcon } from '@mui/icons-material';
import {
  useGetAnnouncementsQuery,
  useCreateAnnouncementMutation,
  useSearchAnnouncementUsersQuery,
} from '../../store/api/adminApi';

interface UserOption {
  id: string;
  username: string;
  email: string;
}

const AnnouncementsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targetType, setTargetType] = useState<'all' | 'individual'>('all');
  const [selectedUsers, setSelectedUsers] = useState<UserOption[]>([]);
  const [userSearchText, setUserSearchText] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  const { data: announcementsData, isLoading: loadingAnnouncements } = useGetAnnouncementsQuery();
  const [createAnnouncement] = useCreateAnnouncementMutation();
  const { data: searchResults } = useSearchAnnouncementUsersQuery(userSearchText, {
    skip: targetType !== 'individual' || userSearchText.length < 1,
  });

  const announcements = announcementsData?.announcements || [];
  const userOptions: UserOption[] = searchResults?.users || [];

  const handleSend = useCallback(async () => {
    if (!title.trim() || !body.trim()) {
      setError('タイトルと本文を入力してください');
      return;
    }
    if (targetType === 'individual' && selectedUsers.length === 0) {
      setError('送信先ユーザーを選択してください');
      return;
    }

    setError('');
    setSuccess('');
    setSending(true);

    try {
      const createResult = await createAnnouncement({
        title: title.trim(),
        body: body.trim(),
        targetType,
        targetUserIds: selectedUsers.map((u) => u.id),
      }).unwrap();

      const totalCount = createResult.totalCount;

      if (totalCount === 0) {
        setSuccess('対象ユーザーが見つかりませんでした');
        setSending(false);
        return;
      }

      setSuccess(`お知らせを作成しました（対象: ${totalCount}名）。通知は自動的に配信されます。`);
      setTitle('');
      setBody('');
      setSelectedUsers([]);
    } catch (err: any) {
      console.error('Failed to create announcement:', err);
      setError(err?.data?.error || 'お知らせの作成に失敗しました');
    } finally {
      setSending(false);
    }
  }, [title, body, targetType, selectedUsers, createAnnouncement]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        <CampaignIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        お知らせ管理
      </Typography>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label="お知らせ作成" />
        <Tab label="送信履歴" />
      </Tabs>

      {tabValue === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              新しいお知らせを作成
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}

            <TextField
              fullWidth
              label="タイトル"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="本文"
              multiline
              rows={6}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>送信先</InputLabel>
              <Select
                value={targetType}
                label="送信先"
                onChange={(e) =>
                  setTargetType(e.target.value as 'all' | 'individual')
                }
              >
                <MenuItem value="all">全ユーザー</MenuItem>
                <MenuItem value="individual">個別ユーザー</MenuItem>
              </Select>
            </FormControl>

            {targetType === 'individual' && (
              <Autocomplete
                multiple
                options={userOptions}
                getOptionLabel={(opt) => `${opt.username} (${opt.email})`}
                value={selectedUsers}
                onChange={(_, newVal) => setSelectedUsers(newVal)}
                inputValue={userSearchText}
                onInputChange={(_, val) => setUserSearchText(val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="ユーザーを検索"
                    placeholder="ユーザー名またはメールで検索"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.username}
                      {...getTagProps({ index })}
                      key={option.id}
                    />
                  ))
                }
                sx={{ mb: 2 }}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
              />
            )}

            <Button
              variant="contained"
              startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
              onClick={handleSend}
              disabled={sending}
              size="large"
            >
              {sending ? '送信中...' : 'お知らせを送信'}
            </Button>
          </CardContent>
        </Card>
      )}

      {tabValue === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              送信履歴
            </Typography>

            {loadingAnnouncements ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>タイトル</TableCell>
                      <TableCell>送信先</TableCell>
                      <TableCell>本文</TableCell>
                      <TableCell>送信日時</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {announcements.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          送信履歴はありません
                        </TableCell>
                      </TableRow>
                    ) : (
                      announcements.map((a: any) => (
                        <TableRow key={a.id}>
                          <TableCell>{a.title}</TableCell>
                          <TableCell>
                            <Chip
                              label={
                                a.targetType === 'all'
                                  ? '全ユーザー'
                                  : `${a.targetUserIds?.length || 0}名`
                              }
                              size="small"
                              color={a.targetType === 'all' ? 'primary' : 'default'}
                            />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 300 }}>
                            <Typography noWrap>{a.body}</Typography>
                          </TableCell>
                          <TableCell>{formatDate(a.createdAt)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default AnnouncementsPage;
